/**
 * +world-resources: loot colours (render-only). Glow colours are linear RGB tuned for additive
 * sprites under the bloom threshold: rarity reads by hue, never by blinding brightness.
 */
import type { PickupRarity, WorldObjectKind, WorldResourceKind } from '@keystone/sim';

export type RGB3 = readonly [number, number, number];

/** Rarity glow / beam colour (common grey-white, uncommon green, rare blue, epic purple, legendary orange). */
export const RARITY_GLOW: Readonly<Record<PickupRarity, RGB3>> = {
  common: [0.78, 0.8, 0.82],
  uncommon: [0.28, 0.95, 0.36],
  rare: [0.28, 0.58, 1.0],
  epic: [0.72, 0.34, 1.0],
  legendary: [1.0, 0.52, 0.12],
};
/** Altar Sigils (not a world drop today, kept for parity with the loot reveal). */
export const SIGIL_GLOW: RGB3 = [1.0, 0.8, 0.3];

/** Beam height (yd) by rarity; 0 = no beam (common loot only glows). */
export const RARITY_BEAM: Readonly<Record<PickupRarity, number>> = { common: 0, uncommon: 1.5, rare: 2.3, epic: 3.1, legendary: 4.2 };
/** Glow strength by rarity (alpha multiplier). */
export const RARITY_GLOW_K: Readonly<Record<PickupRarity, number>> = { common: 0.55, uncommon: 0.7, rare: 0.8, epic: 0.9, legendary: 1.0 };

/** Pickup mesh shape per resource (index into the merged gem geometry, see pickups.ts). */
export const RESOURCE_SHAPE: Readonly<Record<WorldResourceKind, number>> = { gold: 0, fangsteel: 1, scale: 2, scaleUp: 2, venom: 3, moltskin: 4, eyeFragment: 5, xp: 5, item: 2 }; // +field: XP wisp = a bright gem, gear = a scale-like token
/** Per-instance tint on top of the baked shape colour (scaleUp reads a grade richer). */
export const RESOURCE_TINT: Readonly<Record<WorldResourceKind, RGB3>> = {
  gold: [1, 1, 1],
  fangsteel: [1, 1, 1],
  scale: [1, 1, 1],
  scaleUp: [1.05, 0.8, 1.25],
  venom: [1, 1, 1],
  moltskin: [1, 1, 1],
  eyeFragment: [1, 1, 1],
  xp: [0.75, 0.7, 1.5], // +field: violet XP wisp
  item: [1.1, 1.0, 0.85], // +field
};

/** Shatter chunk colours per object kind (sRGB hex; 2-3 per kind, picked per chunk). */
export const CHUNK_COLORS: Readonly<Record<WorldObjectKind, readonly number[]>> = {
  urn: [0xb07a4c, 0x9a6440, 0xc99a45],
  crate: [0x7a5434, 0x6b4a2e, 0x8a6a3e],
  bone_pile: [0xd8ccb0, 0xc4b896, 0xe6dcc4],
  egg_clutch: [0xd8ccb0, 0xe6dcc4, 0x3f8f78],
  cocoon: [0xb8bfa6, 0x9aa38a, 0xd2d6c2],
  offering_bowl: [0xc99a45, 0xa8793a, 0xe0b860],
  hidden_cache: [0x6b4a2e, 0xc99a45, 0x8a6a3e],
  treasure_chest: [0x7a5434, 0xe0b860, 0x8a6a3e], // +field
  venom_gland: [0x4fbf6a, 0x2f8a4a, 0x9be05a],
  eye_shard: [0xffa040, 0xd8782a, 0xffd080],
};

/** Dust puff tint per kind (linear-ish, alpha-blended). */
export const DUST_COLOR: Readonly<Record<WorldObjectKind, RGB3>> = {
  urn: [0.55, 0.45, 0.34],
  crate: [0.5, 0.42, 0.32],
  bone_pile: [0.62, 0.58, 0.5],
  egg_clutch: [0.6, 0.62, 0.5],
  cocoon: [0.62, 0.64, 0.56],
  offering_bowl: [0.58, 0.5, 0.36],
  hidden_cache: [0.5, 0.44, 0.34],
  treasure_chest: [0.62, 0.54, 0.34], // +field
  venom_gland: [0.3, 0.62, 0.32],
  eye_shard: [0.75, 0.55, 0.3],
};

/** Ambient glow for nodes and caches (linear RGB). */
export const NODE_GLOW: Partial<Readonly<Record<WorldObjectKind, RGB3>>> = {
  venom_gland: [0.3, 0.95, 0.4],
  eye_shard: [1.0, 0.55, 0.15],
  hidden_cache: [0.32, 0.86, 0.62],
};

/** Breakables' near-cue rim colour (warm gold) and the hidden-cache hint (jade). */
export const NEAR_RIM: RGB3 = [1.0, 0.82, 0.45];
export const JADE_GLINT: RGB3 = [0.35, 1.0, 0.72];
